import React from 'react';
import styled from 'styled-components';
import { Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { FiLogOut, FiUser } from 'react-icons/fi';

const HeaderContainer = styled.header`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 1000;
  background: transparent;
  backdrop-filter: blur(10px);
`;

const HeaderContent = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  padding: 1rem 0;
  max-width: 1400px;
  margin: 0 auto;
  padding-left: ${props => props.theme.spacing.xl};
  padding-right: ${props => props.theme.spacing.xl};
  
  @media (max-width: ${props => props.theme.breakpoints.tablet}) {
    padding: 1rem ${props => props.theme.spacing.lg};
    justify-content: space-between;
  }
`;

const MobileLogo = styled(Link)`
  display: none;
  font-family: ${props => props.theme.fonts.primary};
  font-size: 1.2rem;
  font-weight: 900;
  color: ${props => props.theme.colors.text};
  text-decoration: none;
  text-transform: uppercase;
  letter-spacing: 2px;
  
  @media (max-width: ${props => props.theme.breakpoints.tablet}) {
    display: block;
  }
`;

const HeaderActions = styled.div`
  display: flex;
  align-items: center;
  gap: ${props => props.theme.spacing.lg};
  
  @media (max-width: ${props => props.theme.breakpoints.tablet}) {
    gap: ${props => props.theme.spacing.md};
  }
`;

const UserButton = styled.button`
  display: flex;
  align-items: center;
  gap: ${props => props.theme.spacing.sm};
  background: ${props => props.theme.colors.surface};
  border: 2px solid ${props => props.theme.colors.divider};
  border-radius: ${props => props.theme.borderRadius.base};
  padding: ${props => props.theme.spacing.sm} ${props => props.theme.spacing.lg};
  color: ${props => props.theme.colors.text};
  font-family: ${props => props.theme.fonts.primary};
  font-size: 0.9rem;
  font-weight: 600;
  cursor: pointer;
  transition: all ${props => props.theme.transitions.normal};
  text-transform: uppercase;
  letter-spacing: 0.5px;
  
  &:hover {
    background: ${props => props.theme.colors.primary};
    color: ${props => props.theme.colors.background};
    border-color: ${props => props.theme.colors.primary};
    transform: translateY(-2px);
  }
  
  .user-avatar {
    width: 28px;
    height: 28px;
    background: ${props => props.theme.colors.primary};
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    color: ${props => props.theme.colors.background};
    font-weight: 700;
    font-size: 0.8rem;
  }
  
  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    padding: ${props => props.theme.spacing.sm};
    
    span {
      display: none;
    }
  }
`;

const LoginLinkButton = styled(Link)`
  display: flex;
  align-items: center;
  gap: ${props => props.theme.spacing.sm};
  background: transparent;
  border: 2px solid ${props => props.theme.colors.primary};
  border-radius: ${props => props.theme.borderRadius.base};
  padding: ${props => props.theme.spacing.sm} ${props => props.theme.spacing.lg};
  color: ${props => props.theme.colors.primary};
  font-family: ${props => props.theme.fonts.primary};
  font-size: 0.9rem;
  font-weight: 700;
  cursor: pointer;
  text-decoration: none;
  transition: all ${props => props.theme.transitions.normal};
  text-transform: uppercase;
  letter-spacing: 0.5px;
  
  &:hover {
    background: ${props => props.theme.colors.primary};
    color: ${props => props.theme.colors.background};
    transform: translateY(-2px);
  }
  
  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    padding: ${props => props.theme.spacing.sm};
    
    span {
      display: none;
    }
  }
`;

const Header = () => {
  const { currentUser, logout } = useAuth();
  
  const handleLogout = async () => {
    try {
      await logout();
    } catch (error) {
      console.error('Failed to log out:', error);
    }
  };
  
  return (
    <HeaderContainer>
      <HeaderContent>
        <MobileLogo to="/">
          SCHURR
        </MobileLogo>
        
        <HeaderActions>
          {currentUser ? (
            <UserButton onClick={handleLogout}>
              <div className="user-avatar">
                {currentUser.email?.charAt(0).toUpperCase()}
              </div>
              <span>Logout</span>
              <FiLogOut size={16} />
            </UserButton>
          ) : (
            <LoginLinkButton to="/login">
              <FiUser size={16} />
              <span>Login</span>
            </LoginLinkButton>
          )}
        </HeaderActions>
      </HeaderContent>
    </HeaderContainer>
  );
};

export default Header;